import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScheduleModal } from './ScheduleModal';
import { MediaPickerModal, type MediaPickerFile } from './MediaPickerModal';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import React, { useEffect, useRef, useState } from 'react';
import { PlatformSelector } from './PlatformSelector';
import {
  AdaptedPlatformContent,
  MediaAttachment,
  PlatformPostSettings,
  SocialMediaAccountPublic,
  SocialMediaPost,
  SocialPlatform,
  TwitterPostSettings,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import {
  ComposerPlatform,
  PLATFORM_MEDIA_LIMITS,
  getEffectiveMediaLimit,
  getEffectivePlatformCharacterLimit,
} from './platform-constants';
import { useMediaUpload } from '../hooks/useMediaUpload';
import { GeneratedImageResult } from '../hooks';
import {
  Loader2,
  X,
  UserPlus,
  VideoIcon,
  AlertCircle,
  FolderOpen,
  Sparkles,
  ChevronDown,
  CalendarsIcon,
} from 'lucide-react';
import { TagSelector } from './TagSelector';
import { AiAssistantPanel, AiAssistantContext } from './AiAssistantPanel';
import { LinkSection } from './LinkSection';
import { PlatformCard } from './PlatformCard';
import { CalendarRemoveIcon, DigitalClockIcon, FloppyIcon, SendIcon } from './Icons';

export interface AdaptContext {
  tone?: string[];
  goal?: string[];
  audience?: string;
  link?: string;
  additionalContext?: string;
  /** Per-platform text already generated/edited — used as reference context when regenerating */
  currentPlatformText?: Record<string, string>;
}

interface PostComposerProps {
  post: SocialMediaPost | null;
  content: string;
  setContent: (content: string) => void;
  title: string;
  setTitle: (title: string) => void;
  notes: string;
  setNotes: (notes: string) => void;
  targetAccountIds: string[];
  setTargetAccountIds: (ids: string[]) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  accounts: SocialMediaAccountPublic[];
  isSaving: boolean;
  error: string | null;
  onSaveDraft: () => void;
  onAdapt: (
    platforms?: string[],
    context?: AdaptContext,
    platformSettings?: Record<string, { format?: string }>,
  ) => void;
  onSchedule: (publishAt: string) => void;
  onPublishNow: (approvedPlatformKeys?: string[]) => void;
  onSavePlatformEdits: (content: Record<string, AdaptedPlatformContent>) => Promise<void>;
  isAdapting: boolean;
  isReadOnly?: boolean;
  editedPlatformText: Record<string, string>;
  onEditedPlatformTextChange: (overrides: Record<string, string>) => void;
  onResetToDraft?: () => void;
  initialMediaFileIds?: string[];
  onMediaChange?: (attachments: MediaAttachment[]) => void;
  link: string;
  setLink: (v: string) => void;
  perPlatformSettings: Record<string, Partial<PlatformPostSettings>>;
  setPerPlatformSettings: React.Dispatch<
    React.SetStateAction<Record<string, Partial<PlatformPostSettings>>>
  >;
  approvedPlatforms: Record<string, boolean>;
  setApprovedPlatforms: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export function PostComposer({
  post,
  content,
  setContent,
  title,
  setTitle,
  notes,
  setNotes,
  targetAccountIds,
  setTargetAccountIds,
  tags,
  onTagsChange,
  accounts,
  isSaving,
  error,
  onSaveDraft,
  onAdapt,
  onSchedule,
  onPublishNow,
  onSavePlatformEdits,
  isAdapting,
  isReadOnly = false,
  editedPlatformText,
  onEditedPlatformTextChange,
  onResetToDraft,
  initialMediaFileIds,
  onMediaChange,
  link,
  setLink,
  perPlatformSettings,
  setPerPlatformSettings,
  approvedPlatforms,
  setApprovedPlatforms,
}: PostComposerProps) {
  const { selectedHolderId } = useHolderSwitcher();
  const [showScheduler, setShowScheduler] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [aiContext, setAiContext] = useState<AiAssistantContext>({
    tone: [],
    goal: [],
    audience: '',
    additionalContext: '',
  });
  const [showAiPanel, setShowAiPanel] = useState(false);

  const { attachments, removeAttachment, addExistingFiles } = useMediaUpload(
    initialMediaFileIds ?? [],
  );

  // Notify parent whenever the attachment list changes
  useEffect(() => {
    onMediaChange?.(attachments);
  }, [attachments, onMediaChange]);

  // Use the joined adaptedAt timestamps as a proxy for "new AI generation happened".
  // This key only changes when Claude returns freshly generated content — NOT when we save edits
  // back to the DB (which preserves the same adaptedAt values).
  const adaptedAtKey = post?.platformContent
    ? Object.values(post.platformContent)
        .map((c) => c.adaptedAt)
        .join(',')
    : '';

  // Tracks the adaptedAtKey value from the previous render so we can distinguish
  // "initial load of existing content" (prevKey === '') from "AI just regenerated" (prevKey !== '').
  const prevAdaptedAtKeyRef = useRef<string>('');

  // Sync platform text and settings whenever adaptedAt changes (AI generated new content)
  // or on the very first time content appears (loading an existing post).
  // Approvals are only RESET when AI genuinely regenerates (prevKey was non-empty and changed).
  // On initial load the parent initialises approvals from the saved post, so we don't touch them.
  useEffect(() => {
    const prevKey = prevAdaptedAtKeyRef.current;
    prevAdaptedAtKeyRef.current = adaptedAtKey;

    if (!post?.platformContent) return;

    const freshText: Record<string, string> = {};
    const freshApproved: Record<string, boolean> = {};
    const aiSettings: Record<string, Partial<PlatformPostSettings>> = {};
    Object.entries(post.platformContent).forEach(([platform, c]) => {
      freshText[platform] = c.text;
      freshApproved[platform] = false;
      // When AI generates thread content, sync threadTweets into perPlatformSettings
      if (c.settings) {
        aiSettings[platform] = c.settings;
      }
    });

    onEditedPlatformTextChange(freshText);

    // Only reset approvals when AI regenerates existing content (not on initial load).
    // On initial load, prevKey is '' and the parent restores approvals from the saved post.
    if (prevKey !== '') {
      setApprovedPlatforms(freshApproved);
    }

    if (Object.keys(aiSettings).length > 0) {
      setPerPlatformSettings((prev) => ({ ...prev, ...aiSettings }));
    }
  }, [adaptedAtKey]); // intentionally omits callbacks — fires only when AI generates new content

  // Initialize perPlatformSettings from saved platformContent settings.
  // Runs on mount / post change; user edits during the session take precedence via spread order.
  useEffect(() => {
    if (!post?.platformContent) return;
    const initial: Record<string, Partial<PlatformPostSettings>> = {};
    Object.entries(post.platformContent).forEach(([platform, c]) => {
      if (c.settings) {
        initial[platform] = c.settings;
      }
    });
    if (Object.keys(initial).length > 0) {
      // initial is the base; current session edits (prev) override so we don't clobber in-flight changes
      setPerPlatformSettings((prev) => ({ ...initial, ...prev }));
    }
  }, [post?.postId]);

  const toggleAccount = (accountId: string) => {
    if (targetAccountIds.includes(accountId)) {
      setTargetAccountIds(targetAccountIds.filter((id) => id !== accountId));
    } else {
      setTargetAccountIds([...targetAccountIds, accountId]);
      // When a platform is selected for the first time, seed its editable text with the
      // current caption so it can be edited independently without touching the main textarea.
      const account = accounts.find((a) => a.accountId === accountId);
      if (account && editedPlatformText[account.platform] === undefined) {
        onEditedPlatformTextChange({ ...editedPlatformText, [account.platform]: content });
      }
    }
  };

  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const hasNoActiveAccounts = activeAccounts.length === 0;

  const selectedPlatforms = Array.from(
    new Set(
      accounts
        .filter((a) => targetAccountIds.includes(a.accountId))
        .map((a) => a.platform as SocialPlatform),
    ),
  );

  const effectiveMediaLimit =
    selectedPlatforms.length > 0
      ? getEffectiveMediaLimit(selectedPlatforms)
      : PLATFORM_MEDIA_LIMITS.twitter;
  const canAddMore = !isReadOnly && attachments.length < effectiveMediaLimit.maxFiles;
  const mediaAccept = effectiveMediaLimit.acceptedMimeTypes.join(',');

  const generatedPlatforms = Object.keys(post?.platformContent ?? {}) as SocialPlatform[];
  const hasPlatformContent = generatedPlatforms.length > 0;

  // Platforms to show cards for: selected platforms union generated platforms
  const platformCardsToShow: ComposerPlatform[] = Array.from(
    new Set([...selectedPlatforms, ...generatedPlatforms]),
  );

  // Approved platform keys
  const approvedKeys = Object.entries(approvedPlatforms)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const hasAnyApproved = approvedKeys.length > 0;

  // Effective character limit for a platform, accounting for the connected
  // account's subscription. X Premium accounts get the 25,000-char long-form
  // limit; standard accounts stay at 280. When multiple accounts of the same
  // platform are targeted, the most restrictive applies (premium only when ALL
  // targeted accounts of that platform are premium) so we never let through a
  // post that a non-premium account would reject.
  const platformCharacterLimit = (platform: ComposerPlatform): number => {
    const platformAccounts = accounts.filter(
      (a) => targetAccountIds.includes(a.accountId) && a.platform === platform,
    );
    const isPremium = platformAccounts.length > 0 && platformAccounts.every((a) => a.isPremium);
    return getEffectivePlatformCharacterLimit(platform, { isPremium });
  };

  // Platforms whose content exceeds their effective character limit. For Twitter
  // threads each tweet is its own post, so each is checked individually.
  const overLimitPlatforms = platformCardsToShow.filter((platform) => {
    const limit = platformCharacterLimit(platform);
    const settings = perPlatformSettings[platform] as Partial<TwitterPostSettings> | undefined;
    if (platform === 'twitter' && settings?.format === 'thread' && settings.threadTweets?.length) {
      return settings.threadTweets.some((t) => t.text.length > limit);
    }
    const text = editedPlatformText[platform] ?? post?.platformContent?.[platform]?.text ?? content;
    return text.length > limit;
  });
  const hasOverLimit = overLimitPlatforms.length > 0;

  // canPublish: always require at least one platform approval when platform cards are visible.
  // This ensures perPlatformSettings (e.g. LinkedIn selectedMediaFileIds) are committed to the DB
  // before publish/schedule regardless of whether AI copy was generated.
  const canPublish =
    !hasNoActiveAccounts &&
    !!content.trim() &&
    selectedPlatforms.length > 0 &&
    (platformCardsToShow.length === 0 || hasAnyApproved) &&
    !hasOverLimit;

  const publishDisabledTitle = hasNoActiveAccounts
    ? 'Connect an account to publish'
    : !content.trim()
      ? 'Write something first'
      : selectedPlatforms.length === 0
        ? 'Select at least one account'
        : platformCardsToShow.length > 0 && !hasAnyApproved
          ? 'Approve at least one platform before publishing'
          : hasOverLimit
            ? `Content exceeds the character limit for ${overLimitPlatforms.join(', ')}`
            : undefined;

  const handleGenerate = () => {
    if (selectedPlatforms.length === 0) return;
    // Extract only the format field per platform — enough for the backend to detect thread format
    const settings = Object.fromEntries(
      Object.entries(perPlatformSettings)
        .filter(([, s]) => (s as Partial<TwitterPostSettings>).format)
        .map(([p, s]) => [p, { format: (s as Partial<TwitterPostSettings>).format }]),
    );
    onAdapt(
      selectedPlatforms,
      {
        tone: aiContext.tone,
        goal: aiContext.goal,
        audience: aiContext.audience || undefined,
        link: link || undefined,
        additionalContext: aiContext.additionalContext || undefined,
      },
      Object.keys(settings).length > 0 ? settings : undefined,
    );
  };

  const handleRegeneratePlatform = (platform: string) => {
    const platformSetting = perPlatformSettings[platform];
    const format = (platformSetting as Partial<TwitterPostSettings>)?.format;
    const currentText = editedPlatformText[platform];
    onAdapt(
      [platform],
      {
        tone: aiContext.tone,
        goal: aiContext.goal,
        audience: aiContext.audience || undefined,
        link: link || undefined,
        additionalContext: aiContext.additionalContext || undefined,
        currentPlatformText: currentText ? { [platform]: currentText } : undefined,
      },
      format ? { [platform]: { format } } : undefined,
    );
  };

  const handleSchedule = (publishAt: string) => {
    onSchedule(publishAt);
    setShowScheduler(false);
  };

  // Build platform content for all visible cards (AI-generated and plain-selected).
  // This persists perPlatformSettings (e.g. LinkedIn selectedMediaFileIds) even when the
  // user writes their own copy without running AI adaptation.
  const buildUpdatedPlatformContent = (): Record<string, AdaptedPlatformContent> => {
    const result: Record<string, AdaptedPlatformContent> = {};
    for (const platform of platformCardsToShow) {
      const original = post?.platformContent?.[platform];
      const editedText = editedPlatformText[platform] ?? original?.text ?? content;
      result[platform] = {
        text: editedText,
        // Hashtags are stored separately and displayed to the user for manual copy-paste.
        // They are never automatically appended to the editable text or the published copy.
        hashtags: original?.hashtags ?? [],
        characterCount: editedText.length,
        suggestions: original?.suggestions ?? [],
        adaptedAt: original?.adaptedAt ?? new Date().toISOString(),
        postType: original?.postType,
        settings: perPlatformSettings[platform] as PlatformPostSettings | undefined,
      };
    }
    return result;
  };

  // Always filter publish accounts to only those on approved platforms.
  const getPublishAccountIds = (): string[] => {
    return accounts
      .filter(
        (a) => targetAccountIds.includes(a.accountId) && approvedPlatforms[a.platform as string],
      )
      .map((a) => a.accountId);
  };

  // Save platform content edits (text + settings) before persisting the draft
  const handleSaveDraft = async () => {
    if (platformCardsToShow.length > 0 && !isReadOnly && post) {
      await onSavePlatformEdits(buildUpdatedPlatformContent());
    }
    onSaveDraft();
  };

  const handlePublish = async () => {
    if (platformCardsToShow.length > 0 && !isReadOnly) {
      await onSavePlatformEdits(buildUpdatedPlatformContent());
    }
    const approvedAccountIds = getPublishAccountIds();
    onPublishNow(approvedAccountIds.length > 0 ? approvedAccountIds : undefined);
  };

  const handleScheduleWithEdits = async (publishAt: string) => {
    if (platformCardsToShow.length > 0 && !isReadOnly) {
      await onSavePlatformEdits(buildUpdatedPlatformContent());
    }
    handleSchedule(publishAt);
  };

  /**
   * Bridge the picker's `MediaPickerFile` shape onto the existing
   * `addExistingFiles` consumer (which expects an object with
   * `previewUrl`). The Vault picker already hands us a fresh signed
   * download URL — no resolution round-trip needed.
   */
  const handleBrowseFilesSelected = async (files: MediaPickerFile[]) => {
    void addExistingFiles(
      files.map((f) => ({
        fileId: f.fileId,
        fileName: f.fileName,
        mimeType: f.mimeType,
        sizeBytes: f.sizeBytes,
        previewUrl: f.downloadUrl,
      })),
    );
  };

  const handleImageGenerated = (result: GeneratedImageResult) => {
    if (!result.fileId) return;
    void addExistingFiles([
      {
        fileId: result.fileId,
        fileName: `ai-generated-${Date.now()}.png`,
        mimeType: 'image/png',
        sizeBytes: 0,
        previewUrl: result.imageUrl,
      },
    ]);
  };

  const renderStatusBanner = () => {
    const status = post?.status;
    if (!status || status === 'draft') return null;

    if (status === 'scheduled') {
      const formattedDate = new Date(post!.publishAt).toLocaleString();
      return (
        <div className="p-3 rounded-md bg-muted border border-border text-sm text-foreground">
          Scheduled to publish on <strong>{formattedDate}</strong>. You can reschedule, publish
          immediately, or unschedule to return to draft.
        </div>
      );
    }
    if (status === 'published') {
      return (
        <div className="p-3 rounded-md bg-muted border border-border text-sm text-muted-foreground">
          This post has been published. Content cannot be edited, but you can repost or schedule a
          future repost.
        </div>
      );
    }
    if (status === 'failed') {
      return (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive">
          Publishing failed. Edit the post and try again, or save as draft to revisit later.
        </div>
      );
    }
    if (status === 'publishing') {
      return (
        <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Publishing in progress…
        </div>
      );
    }
    if (status === 'cancelled') {
      return (
        <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground">
          This post was cancelled. You can edit and republish.
        </div>
      );
    }
    return null;
  };

  const renderActions = () => {
    const status = post?.status;

    if (status === 'publishing') {
      return (
        <span className="text-sm text-muted-foreground flex items-center gap-1.5">
          <Loader2 className="h-4 w-4 animate-spin" />
          Publishing in progress…
        </span>
      );
    }

    if (status === 'published') {
      return (
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePublish}
            disabled={isSaving || selectedPlatforms.length <= 0}
            size="sm"
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <SendIcon />}
            Repost Now
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowScheduler(true)}
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
          >
            <CalendarsIcon size={13} />
            Reschedule
          </Button>
        </div>
      );
    }

    if (status === 'scheduled') {
      return (
        <div className="flex items-center gap-2">
          <Button
            onClick={onResetToDraft}
            disabled={isSaving}
            size="sm"
            variant="outline"
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <CalendarRemoveIcon size={13} />
            )}
            Unschedule
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={hasNoActiveAccounts}
            onClick={() => setShowScheduler(true)}
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
          >
            <CalendarsIcon size={13} />
            Reschedule
          </Button>
          <Button
            onClick={handlePublish}
            disabled={!canPublish}
            size="sm"
            title={publishDisabledTitle}
            className="self-start shrink-0 !font-mono !py-1.25 !px-2 text-[10px] h-auto uppercase"
          >
            <SendIcon size={14} />
            Publish Now
          </Button>
        </div>
      );
    }

    if (status === 'failed') {
      return (
        <div className="flex items-center gap-2">
          <Button
            onClick={onResetToDraft}
            disabled={isSaving}
            size="sm"
            variant="outline"
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <FloppyIcon size={13} />
            )}
            Save as Draft
          </Button>
          <Button
            onClick={handlePublish}
            disabled={!canPublish}
            size="sm"
            title={publishDisabledTitle}
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
          >
            <SendIcon size={13} />
            Publish Now
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={hasNoActiveAccounts}
            onClick={() => setShowScheduler(true)}
          >
            <DigitalClockIcon size={13} />
            Schedule Post
          </Button>
        </div>
      );
    }

    // Default: draft / new / cancelled
    return (
      <div className="flex items-center gap-2">
        <Button
          onClick={() => void handleSaveDraft()}
          disabled={isSaving || !content.trim() || selectedPlatforms.length <= 0}
          size="sm"
          variant="outline"
          className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FloppyIcon size={13} />}
          Save Draft
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={hasNoActiveAccounts || isSaving || !canPublish}
          onClick={() => setShowScheduler(true)}
          className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
        >
          <DigitalClockIcon size={13} />
          Schedule
        </Button>
        <Button
          onClick={handlePublish}
          disabled={isSaving || !canPublish}
          size="sm"
          title={publishDisabledTitle}
          className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto uppercase"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <SendIcon size={13} />}
          Publish Now
        </Button>
      </div>
    );
  };

  const doneAttachments = attachments.filter((a) => a.uploadState === 'done');

  return (
    <div className="flex flex-col gap-5 max-w-[720px]">
      {renderStatusBanner()}

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      {/* 1. Title / Campaign Name */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Title / campaign name{' '}
          <span className="normal-case font-normal text-muted-foreground/60">(optional)</span>
        </p>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Give this post a name for your reference…"
          disabled={isReadOnly}
          className={cn(
            'text-sm',
            'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            'h-[40px] px-3 py-3 !font-mono',
            'text-primary',
            'placeholder-[var(--input-placeholder)]',
          )}
        />
      </div>

      {/* 2. Post to */}
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Post to
        </p>
        {accounts.length === 0 ? (
          <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground">
            No connected accounts.{' '}
            <Link
              to="/a/arche_social_media/accounts"
              className="underline text-foreground hover:text-primary"
            >
              Connect one on the Accounts page
            </Link>{' '}
            before publishing.
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <PlatformSelector
              accounts={accounts}
              selectedAccountIds={targetAccountIds}
              onToggle={isReadOnly ? () => undefined : toggleAccount}
            />
            <Link
              to="/a/arche_social_media/accounts"
              className={cn(
                'flex items-center gap-1 rounded-lg border border-dashed border-border py-1 px-2',
                'text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30',
                'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <UserPlus className="h-4 w-4" />
              <span>Add Account</span>
            </Link>
          </div>
        )}
      </div>

      {/* 3. Caption */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Caption
          </p>
          <span className="text-xs text-muted-foreground !font-mono">
            {content.length} characters
          </span>
        </div>
        <Textarea
          placeholder="What's on your mind? Write your post here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          className={cn(
            'text-sm resize-none',
            'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            'px-3 py-3 !font-mono',
            'text-primary',
            'placeholder-[var(--input-placeholder)]',
          )}
          disabled={isReadOnly}
          readOnly={isReadOnly}
        />
      </div>

      {/* 4. AI Writing Assistant Panel */}
      {!isReadOnly && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowAiPanel((prev) => !prev)}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5" />
            AI writing assistant (Per Post Overwride)
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', showAiPanel && 'rotate-180')}
            />
          </button>
          {showAiPanel && (
            <AiAssistantPanel
              value={aiContext}
              onChange={setAiContext}
              disabled={isAdapting}
              canGenerate={selectedPlatforms.length > 0 && !!content.trim()}
              handleGenerate={handleGenerate}
            />
          )}
        </div>
      )}

      {/* 5. Media — hidden when read-only with no attachments */}
      {(!isReadOnly || attachments.length > 0) && (
        <div className="space-y-3 mt-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Media
            </p>
            {attachments.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {attachments.length}/{effectiveMediaLimit.maxFiles} files
                {selectedPlatforms.length > 0 && ` (${selectedPlatforms.join(', ')} limit)`}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.fileId}
                className="relative h-20 w-20 rounded-lg overflow-hidden border border-border flex-shrink-0 bg-muted"
              >
                {attachment.mimeType.startsWith('image/') && attachment.previewUrl ? (
                  <img
                    src={attachment.previewUrl}
                    alt={attachment.fileName}
                    className="w-full h-full object-cover"
                  />
                ) : attachment.mimeType.startsWith('video/') && attachment.previewUrl ? (
                  <video
                    src={attachment.previewUrl}
                    preload="metadata"
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-1">
                    <VideoIcon className="h-6 w-6 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground text-center leading-tight line-clamp-2">
                      {attachment.fileName}
                    </span>
                  </div>
                )}
                {attachment.uploadState === 'uploading' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Loader2 className="h-5 w-5 animate-spin text-white" />
                  </div>
                )}
                {attachment.uploadState === 'error' && (
                  <div
                    className="absolute inset-0 flex items-center justify-center bg-destructive/60"
                    title={attachment.errorMessage}
                  >
                    <AlertCircle className="h-5 w-5 text-white" />
                  </div>
                )}
                {!isReadOnly && attachment.uploadState === 'done' && (
                  <button
                    type="button"
                    onClick={() => removeAttachment(attachment.fileId)}
                    aria-label={`Remove ${attachment.fileName}`}
                    className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {canAddMore && (
              <button
                type="button"
                onClick={() => setShowMediaPicker(true)}
                className="h-20 w-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center hover:bg-muted/50 transition-colors"
              >
                <div className="flex flex-col items-center gap-1">
                  <FolderOpen className="h-4 w-4 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground leading-tight">Browse</span>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* 6. Link */}
      {!isReadOnly && <LinkSection value={link} onChange={setLink} disabled={isSaving} />}

      {/* 7. Platform Settings & AI-Adapted Content */}
      {platformCardsToShow.length > 0 && (
        <div className="flex flex-col gap-4 mt-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Platform settings & AI-adapted content
            </p>
            {!isReadOnly && (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={
                  isAdapting || !content.trim() || isSaving || selectedPlatforms.length === 0
                }
                title={
                  selectedPlatforms.length === 0
                    ? 'Select at least one account to generate'
                    : undefined
                }
                className={cn(
                  'flex items-center gap-1.5 text-xs font-medium transition-colors',
                  'text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed',
                )}
              >
                {isAdapting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                {isAdapting ? 'Generating…' : '✦ Regenerate all'}
              </button>
            )}
          </div>

          {platformCardsToShow.map((platform) => {
            const platformAccount = accounts.find(
              (a) => targetAccountIds.includes(a.accountId) && a.platform === platform,
            );
            return (
              <PlatformCard
                key={platform}
                platform={platform}
                holderId={selectedHolderId}
                postId={post?.postId}
                handle={
                  platformAccount?.platformUsername ?? platformAccount?.platformName ?? undefined
                }
                platformAccountId={platformAccount?.accountId}
                link={link || undefined}
                aiContent={post?.platformContent?.[platform]}
                originalContent={content}
                aiContext={{
                  tone: aiContext.tone,
                  goal: aiContext.goal,
                  audience: aiContext.audience || undefined,
                }}
                editedText={editedPlatformText[platform] ?? ''}
                onEditedTextChange={(text) =>
                  onEditedPlatformTextChange({ ...editedPlatformText, [platform]: text })
                }
                isApproved={approvedPlatforms[platform] ?? false}
                onApprove={(approved) =>
                  setApprovedPlatforms((prev) => ({ ...prev, [platform]: approved }))
                }
                settings={perPlatformSettings[platform] ?? {}}
                onSettingsChange={(s) =>
                  setPerPlatformSettings((prev) => ({ ...prev, [platform]: s }))
                }
                onRegenerate={
                  hasPlatformContent ? () => handleRegeneratePlatform(platform) : undefined
                }
                isAdapting={isAdapting}
                disabled={isReadOnly}
                mediaAttachments={doneAttachments}
                characterLimit={platformCharacterLimit(platform)}
              />
            );
          })}
        </div>
      )}

      {/* Approval status summary — shown whenever platform cards are visible */}
      {platformCardsToShow.length > 0 && !isReadOnly && (
        <div
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-2 text-xs border',
            hasAnyApproved
              ? approvedKeys.length === platformCardsToShow.length
                ? 'bg-primary/5 border-primary/30 text-primary'
                : 'bg-muted border-border text-muted-foreground'
              : 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/20 dark:border-amber-800 dark:text-amber-400',
          )}
        >
          <span className="font-medium">
            {approvedKeys.length}/{platformCardsToShow.length} platform
            {platformCardsToShow.length !== 1 ? 's' : ''} approved
          </span>
          {!hasAnyApproved && <span>— approve at least one platform before publishing</span>}
        </div>
      )}

      {/* 8. Tags */}
      <div className="space-y-2 my-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tags</p>
        <TagSelector tags={tags} onChange={onTagsChange} disabled={isReadOnly} />
      </div>

      {/* 9. Notes (internal) */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Notes{' '}
          <span className="normal-case font-normal text-muted-foreground/60">(internal only)</span>
        </p>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add internal notes, approver context, or campaign details…"
          rows={2}
          className={cn(
            'flex items-center gap-1 focus-within:ring-1 focus-within:ring-ring resize-none text-sm',
            'w-full text-[12px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            'px-3 py-3 !font-mono',
            'text-primary',
            'bg-background  placeholder-gray-500',
          )}
          disabled={isReadOnly}
          readOnly={isReadOnly}
        />
      </div>

      {/* 10. Footer actions */}
      <div className="flex items-center gap-2">{renderActions()}</div>

      <ScheduleModal
        isOpen={showScheduler}
        onClose={() => setShowScheduler(false)}
        onSchedule={handleScheduleWithEdits}
        isLoading={isSaving}
        initialDate={post?.publishAt ?? undefined}
      />
      <MediaPickerModal
        open={showMediaPicker}
        onOpenChange={setShowMediaPicker}
        onFilesSelected={handleBrowseFilesSelected}
        onImageGenerated={handleImageGenerated}
        acceptedMimeTypes={mediaAccept}
        remainingSlots={effectiveMediaLimit.maxFiles - attachments.length}
      />
    </div>
  );
}
